import { usePassThrough } from 'primevue/passthrough';
import { selectField } from '../shared/select-field';

export const select = usePassThrough(selectField, {
  root: ({ props, parent }: Record<string, any>) => ({
    class: [
      'inline-flex',
      // Display and Position
      'relative',
      'rounded-lg',
      // Shape
      'min-w-0 group-[&>*:not(:first-child)]/input-group:rounded-l-none group-[&>*:not(:last-child)]/input-group:rounded-r-none',
      {
        'first:ml-0 ml-[-1px]': parent.instance.$name === 'InputGroup' && !props.showButtons,
      },
      'w-80 max-w-full',
      // Color and Background
      'border border-solid border-grey-300',
      {
        'bg-white': !props.disabled && !props.invalid,
        'bg-grey-50': props.disabled,
        'bg-red-100/50': props.invalid,
        'text-grey-400': props.disabled,
        'text-grey-800': !props.disabled,
      },
      // Invalid State
      { 'border-red-500': props.invalid },
      // Transitions
      'transition-all',
      'duration-200',
      // States
      { 'hover:border-surface-400': !props.invalid },
      // Misc
      'cursor-pointer',
      'select-none',
      {
        'bg-surface-200 select-none pointer-events-none cursor-default': props.disabled,
      },
    ],
  }),
  label: ({ props, parent }: Record<string, any>) => ({
    class: [
      // Font
      'leading-[normal]',
      {
        'text-xs': props.size === 'small',
        'text-xl': props.size === 'large',
      },
      // Display
      'block',
      'flex-auto',
      // Color and Background
      'bg-transparent',
      'border-0',
      {
        'text-surface-800': props.modelValue != null,
        'text-grey-400': props.modelValue == null,
      },
      // Sizing and Spacing
      'w-[1%]',
      'em:py-2 em:px-3 pr-0',
      { 'pr-7': props.showClear },
      // Shape
      'rounded-none',
      // Transitions
      'transition',
      'duration-200',
      // States
      'focus:outline-none focus:shadow-none',
      // Filled State *for FloatLabel
      {
        filled:
          (!parent.instance ? '' : parent.instance.$name) === 'FloatLabel' &&
          props.modelValue !== null,
      },
      // Misc
      'relative',
      'cursor-pointer',
      'overflow-hidden overflow-ellipsis',
      'whitespace-nowrap',
      'appearance-none',
    ],
  }),
  dropdown: ({ props }: Record<string, any>) => ({
    class: [
      { hidden: props.disabled },
      'flex items-center justify-center',
      'shrink-0',
      'bg-transparent',
      'text-surface-500',
      'w-12',
      'rounded-r-md',
    ],
  }),
  overlay: {
    class: [
      'box-content',
      'bg-white',
      'border border-solid border-grey-200',
      'rounded-lg',
      'shadow',
      'text-[16px] font-sans font-normal text-black',
    ],
  },
  listContainer: {
    class: ['max-h-[200px]', 'overflow-auto'],
  },
  list: {
    class: 'py-2 list-none m-0',
  },
  option: ({ context }: Record<string, any>) => ({
    class: [
      // Font
      'leading-none',
      { 'font-normal': !context.selected, 'font-bold': context.selected },
      // Position
      'relative',
      // Shape
      'border-0',
      'rounded-none',
      // Spacing
      'm-0',
      'py-2 px-4',
      // Color
      {
        'bg-primary-300': context.focused && !context.selected,
      },
      {
        'bg-primary-300': context.focused && context.selected,
      },
      { 'bg-transparent': !context.focused && context.selected },
      // States
      'hover:bg-primary-300',
      // Misc
      'cursor-pointer',
      'overflow-hidden',
      'whitespace-nowrap',
      'group/option',
    ],
  }),
  optionGroup: {
    class: ['font-semibold', 'm-0 py-2 px-3', 'text-surface-400', 'cursor-auto'],
  },
  optionCheckIcon: 'relative -ms-1.5 me-1.5 text-surface-700 w-4 h-4',
  optionBlankIcon: 'w-4 h-4',
  emptyMessage: {
    class: ['leading-none', 'py-2 px-3', 'text-surface-800', 'bg-transparent'],
  },
  header: {
    class: [
      'pt-2 px-2 pb-0',
      'm-0',
      'border-b-0',
      'rounded-tl-md',
      'rounded-tr-md',
      'text-surface-700',
      'bg-surface-0',
      'border-surface-300',
    ],
  },
  clearIcon: {
    class: ['text-surface-400', 'absolute', 'top-1/2', 'right-12', '-translate-y-1/2'],
  },
  loadingIcon: {
    class: 'text-surface-400 animate-spin',
    'data-testid': 'v-select-loader',
  },
  transition: {
    enterFromClass: 'opacity-0 scale-y-[0.8]',
    enterActiveClass:
      'transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]',
    leaveActiveClass: 'transition-opacity duration-100 ease-linear',
    leaveToClass: 'opacity-0',
  },
});
